// -----------------------------------------------------------------------------
// Login — Écran de connexion (email + mot de passe)
// StockPro / VisualisPro
// -----------------------------------------------------------------------------

import { useState } from "react";
import { signInWithEmailAndPassword } from "firebase/auth";

import { auth } from "../firebase";
import { T } from "../i18n/translations";
import { buildCSS } from "../styles/theme";
import { Ic, ICONS } from "./Icons";

export default function Login({ lang, setLang, dark, setDark }) {
  const t = T[lang];
  const [email, setEmail] = useState("");
  const [pass, setPass] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [showP, setShowP] = useState(false);
  const isRtl = lang === "ar";

  const handle = async () => {
    if (!email || !pass) return setErr(t.fillFields);
    setLoading(true);
    setErr("");
    try {
      await signInWithEmailAndPassword(auth, email.trim(), pass);
      // useAuth() dans App gère la suite
    } catch (e) {
      setErr(t.wrongCreds);
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight:"100vh", background:dark?"#070711":"#f0f2f8", display:"flex", alignItems:"center", justifyContent:"center" }} dir={isRtl?"rtl":"ltr"}>
      <style>{buildCSS(dark)}</style>
      <div style={{ position:"fixed", inset:0, background:dark?"radial-gradient(ellipse at 40% 30%, #1a0f05 0%, #070711 60%)":"radial-gradient(ellipse at 40% 30%, #fff5f0 0%, #f0f2f8 60%)", pointerEvents:"none" }} />

      {/* Lang + Theme toggles */}
      <div style={{ position:"fixed", top:16, right:16, display:"flex", gap:8, zIndex:10 }}>
        <button onClick={()=>setLang(lang==="fr"?"ar":"fr")} style={{ padding:"6px 12px", borderRadius:8, background:dark?"rgba(255,255,255,.08)":"rgba(0,0,0,.06)", color:dark?"#aaa":"#666", fontSize:12, fontWeight:600, border:"none", cursor:"pointer" }}>
          {lang==="fr"?"🇸🇦 عربي":"🇫🇷 FR"}
        </button>
        <button onClick={()=>setDark(!dark)} style={{ width:32, height:32, borderRadius:8, background:dark?"rgba(255,255,255,.08)":"rgba(0,0,0,.06)", border:"none", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center" }}>
          <Ic d={dark?ICONS.sun:ICONS.moon} size={15} color={dark?"#aaa":"#666"} />
        </button>
      </div>

      <div className="fade-up" style={{ position:"relative", zIndex:1, width:"100%", maxWidth:380, padding:"0 20px" }}>
        {/* Logo */}
        <div style={{ textAlign:"center", marginBottom:32 }}>
          <div style={{ display:"inline-flex", alignItems:"center", gap:11, marginBottom:12 }}>
            <div style={{ width:44, height:44, background:"linear-gradient(135deg,#FF6B35,#f7931e)", borderRadius:13, display:"flex", alignItems:"center", justifyContent:"center" }}>
              <Ic d={ICONS.box} size={22} color="#fff" />
            </div>
            <div style={{ textAlign:isRtl?"right":"left" }}>
              <div style={{ fontSize:22, fontWeight:800, color:dark?"#fff":"#1e293b" }}>{t.appName}</div>
              <div style={{ fontSize:10, color:dark?"#333":"#aaa", letterSpacing:"1.5px" }}>{t.by}</div>
            </div>
          </div>
          <div style={{ display:"inline-flex", alignItems:"center", gap:6, background:"rgba(16,185,129,.1)", border:"1px solid rgba(16,185,129,.2)", borderRadius:20, padding:"4px 14px" }}>
            <Ic d={ICONS.shield} size={12} color="#10b981" />
            <span style={{ fontSize:11, color:"#10b981", fontWeight:600 }}>{t.licenceTrialInfo}</span>
          </div>
        </div>

        {/* Form */}
        <div className="card" style={{ padding:"26px 22px" }}>
          {/* Email */}
          <div style={{ marginBottom:14 }}>
            <label style={{ display:"block", fontSize:11, color:dark?"#555":"#aaa", marginBottom:6, letterSpacing:"1px", textTransform:"uppercase" }}>{t.email}</label>
            <div style={{ position:"relative" }}>
              <div style={{ position:"absolute", left:11, top:"50%", transform:"translateY(-50%)" }}><Ic d={ICONS.mail} size={14} color="#888" /></div>
              <input className="fi" value={email} onChange={e=>setEmail(e.target.value)} placeholder="email@restaurant.com" style={{ paddingLeft:34 }} onKeyDown={e=>e.key==="Enter"&&handle()} />
            </div>
          </div>
          {/* Password */}
          <div style={{ marginBottom:18 }}>
            <label style={{ display:"block", fontSize:11, color:dark?"#555":"#aaa", marginBottom:6, letterSpacing:"1px", textTransform:"uppercase" }}>{t.password}</label>
            <div style={{ position:"relative" }}>
              <div style={{ position:"absolute", left:11, top:"50%", transform:"translateY(-50%)" }}><Ic d={ICONS.lock} size={14} color="#888" /></div>
              <input className="fi" type={showP?"text":"password"} value={pass} onChange={e=>setPass(e.target.value)} onKeyDown={e=>e.key==="Enter"&&handle()} style={{ paddingLeft:34, paddingRight:38 }} />
              <button onClick={()=>setShowP(!showP)} style={{ position:"absolute", right:11, top:"50%", transform:"translateY(-50%)", background:"none", border:"none", cursor:"pointer" }}>
                <Ic d={ICONS.eye} size={14} color="#666" />
              </button>
            </div>
          </div>
          {err && (
            <div style={{ background:"rgba(239,68,68,.1)", border:"1px solid rgba(239,68,68,.2)", borderRadius:8, padding:"8px 12px", color:"#f87171", fontSize:13, marginBottom:12 }}>{err}</div>
          )}
          <button className="glow-btn" onClick={handle} disabled={loading} style={{ width:"100%", padding:"12px", background:"linear-gradient(135deg,#FF6B35,#f7931e)", borderRadius:10, color:"#fff", fontWeight:700, fontSize:15, border:"none", cursor:"pointer" }}>
            {loading
              ? <span style={{ display:"inline-block", width:16, height:16, border:"2px solid rgba(255,255,255,.3)", borderTopColor:"#fff", borderRadius:"50%", animation:"spin .7s linear infinite" }} />
              : t.login
            }
          </button>

          {/* Firebase info */}
          <div style={{ marginTop:14, padding:"10px 12px", background:dark?"rgba(255,255,255,.02)":"rgba(0,0,0,.03)", borderRadius:9, border:`1px solid ${dark?"rgba(255,255,255,.05)":"rgba(0,0,0,.06)"}` }}>
            <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:4 }}>
              <Ic d={ICONS.cloud} size={12} color="#60a5fa" />
              <span style={{ fontSize:11, color:"#60a5fa", fontWeight:600 }}>Données synchronisées sur le cloud</span>
            </div>
            <p style={{ fontSize:11, color:dark?"#333":"#bbb" }}>Connectez-vous avec l'email fourni par VisualisPro lors de l'activation de votre licence.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
